// =========================================================================
// Projections for the Home section landing. Route `/home`, Full mode only.
//
// Demo source: `SECPAGE.home` (line 3314) and `renderSectionPage`
// (line 3364). The lead, the four stats and the eight tiles are reproduced
// exactly, in the demo's order; the tile glyphs are the demo's own nav glyphs
// (lines 1354–1361), which is what its `vIcon()` read back out of the DOM.
//
// Transformation layer — pure functions of explicit arguments. No React,
// no fetching, no side effects.
// Fork-owned (zone C), L5 lane.
// =========================================================================

import { FLEET, MINE_ORDER } from '@/versions/v1/data/core';
import { OEM_STATIC_COUNTS, PLATFORM_COUNTS } from '@/versions/v1/shared/oem-counts';
import { routeForView } from '@/versions/v1/shared/oem-routes';
import { ALERTS } from '@/versions/v1/shared/seed/alerts';
import { INCIDENTS } from '@/versions/v1/shared/seed/incidents';
import type { HeroStat } from './SectionHero';

/** The section's own colour — `SECPAGE.home.col`. */
export const HOME_COLOUR = '#1C3552';

/** The section mark — the demo's `data-section="home"` nav glyph. */
export const HOME_ICON = '<path d="M3 11l9-7 9 7v9a1 1 0 01-1 1h-5v-6H9v6H4a1 1 0 01-1-1z"/>';

/** Verbatim from `SECPAGE.home.tag`. */
export const HOME_TAG = 'the whole operation on one screen';

/** `SECPAGE.home.lead()` — verbatim, with the agent and system counts derived. */
export const homeLead = (): string =>
  'Everything the network is doing this morning in one place. Asset360 is the operating model — ' +
  `a control tower over ten modules and five feedback loops, run by ${PLATFORM_COUNTS.agents} agents ` +
  `over ${PLATFORM_COUNTS.connectors} connected systems. Start here for what the fleet is doing ` +
  'this morning, then follow a job, a machine or a site down as far as you need to go.';

/**
 * The hero's four figures — `SECPAGE.home.stats()`.
 *
 * `openJobs` and `availability` are `null` until their domains land, and print
 * as a dash rather than as a fallback: a hero figure that later changes under
 * the reader is worse than one that arrives late.
 */
export function homeStats(
  openJobs: number | null,
  availability: number | null,
): readonly HeroStat[] {
  return [
    {
      eyebrow: 'Machines',
      value: String(FLEET.length),
      detail: `${MINE_ORDER.length} Australian mines`,
    },
    {
      eyebrow: 'Open jobs',
      value: openJobs === null ? '—' : String(openJobs),
      detail: 'across ten modules',
    },
    {
      eyebrow: 'Open alerts',
      value: String(ALERTS.length),
      detail: `${INCIDENTS.length} incidents this fortnight`,
    },
    {
      eyebrow: 'Fleet availability',
      value: availability === null ? '—' : `${availability}%`,
      detail: 'week to 06 Sep',
    },
  ];
}

/** One tile on the landing grid. */
export interface HomeTile {
  /** The demo view the tile `show()`s. */
  readonly id: string;
  readonly label: string;
  readonly description: string;
  /** Right-aligned figure, already formatted. */
  readonly count: string;
  /** Resolved through `routeForView`; the eight views all have a route. */
  readonly to: string;
  /** Inline SVG children on a `0 0 24 24` viewBox. */
  readonly icon: string;
}

/** The eight tiles, as `[view, label, description, icon]` in demo order. */
const TILE_SEED: readonly (readonly [string, string, string, string])[] = [
  [
    'asset360',
    'Asset360',
    'The control tower, the ten modules, the feedback loops, the agents and the systems underneath.',
    '<circle cx="12" cy="12" r="8.6"/><circle cx="12" cy="12" r="3"/><path d="M12 3.4v3.2M12 17.4v3.2M3.4 12h3.2M17.4 12h3.2"/>',
  ],
  [
    'overview',
    'Sites map',
    'Where the fleet is: six mines, their condition, availability and open work.',
    '<path d="M9 4L3 6.5v13L9 17l6 2.5 6-2.5v-13L15 6.5z"/><path d="M9 4v13M15 6.5v13"/>',
  ],
  [
    'customers',
    'Customers',
    'Who each site is, the contract behind it, the contacts and what they have said about the service.',
    '<path d="M3 21V8l6-4 6 4v13"/><path d="M15 21V11h6v10"/><path d="M6.5 10h2M6.5 14h2M18 15h1"/>',
  ],
  [
    'jobboard',
    'Job board',
    'Every alert, case, agent draft and watch condition as one board, by module and by owner.',
    '<rect x="3" y="4" width="5" height="16" rx="1"/><rect x="10" y="4" width="5" height="10" rx="1"/><rect x="17" y="4" width="4" height="13" rx="1"/>',
  ],
  [
    'fleet',
    'Fleet health',
    'Every machine by mine, with its key metric charted over the last twelve days.',
    '<path d="M3 17l5-6 4 3 4-7 5 5"/><path d="M3 21h18"/>',
  ],
  [
    'sos',
    'S.O.S report',
    'This morning’s service operations summary — hotspots by site and by class.',
    '<rect x="4" y="3" width="16" height="18" rx="2"/><path d="M8 8h8M8 12h8M8 16h5"/>',
  ],
  [
    'approvals',
    'Approvals',
    'Everything the agents have drafted and nothing has touched a machine yet.',
    '<path d="M4 12l5 5L20 6"/>',
  ],
  [
    'costs',
    'Cost summary',
    'Every dollar traced to a work order, a machine, a crew and a supplier, with diesel, power and emissions.',
    '<path d="M12 3v18M8 7h6a3 3 0 010 6H9a3 3 0 000 6h7"/>',
  ],
];

/**
 * The eight tiles — `SECPAGE.home.tiles()`.
 *
 * Counts are the sidebar's own badges from `oem-counts.ts`, so a tile and the
 * rail can never disagree. The job board is the one live count: it takes the
 * assembled board when it has landed and the static fallback until then.
 * Costs carries the demo's literal `'18 m'` — the ledger's span, not a count.
 */
export function homeTiles(openJobs: number | null): readonly HomeTile[] {
  const counts: Readonly<Record<string, string>> = {
    asset360: String(PLATFORM_COUNTS.modules),
    overview: String(MINE_ORDER.length),
    customers: String(OEM_STATIC_COUNTS.customers),
    jobboard: String(openJobs ?? OEM_STATIC_COUNTS['job-board']),
    fleet: String(OEM_STATIC_COUNTS['fleet-health']),
    sos: String(OEM_STATIC_COUNTS['sos-report']),
    approvals: String(OEM_STATIC_COUNTS.approvals),
    costs: '18 m',
  };
  return TILE_SEED.map(([id, label, description, icon]) => ({
    id,
    label,
    description,
    count: counts[id],
    to: routeForView(id) ?? '/',
    icon,
  }));
}
